/**
 * Cart Yardage Display Updater
 * Custom +/- buttons that update yardage display and sync with FSA
 */

(function() {
  'use strict';

  const CONFIG = {
    yardsToUnitsMultiplier: 2, // 1 unit = 0.5 yards, so 2 units = 1 yard
    maxRetries: 3, // Max retry attempts for failed requests
    retryDelay: 1000, // Initial delay between retries (ms)
  };

  // Track pending requests to prevent race conditions
  const pendingRequests = new Map();

  /**
   * Retry helper with exponential backoff
   */
  async function retryWithBackoff(fn, maxRetries = CONFIG.maxRetries, delay = CONFIG.retryDelay) {
    let lastError;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        // Check if it's a network error (retry) or inventory error (don't retry)
        if (error.message && error.message.includes('inventory')) {
          throw error; // Don't retry inventory errors
        }
        if (attempt < maxRetries - 1) {
          const waitTime = delay * Math.pow(2, attempt); // Exponential backoff
          console.log(`[Cart Yardage] Retry attempt ${attempt + 1}/${maxRetries - 1} in ${waitTime}ms...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }
    throw lastError;
  }

  /**
   * Update yardage display based on quantity
   */
  function updateYardageDisplay(cartItem, quantity) {
    const yardageDisplay = cartItem.querySelector('.cart-yardage-display');
    if (!yardageDisplay) return;

    // Convert units to yards
    const yards = quantity / CONFIG.yardsToUnitsMultiplier;

    // Format display text
    const yardText = yards === 1 ? 'yard' : 'yards';
    yardageDisplay.textContent = `${yards} ${yardText}`;

    // Store quantity in data attribute for reliable state management
    yardageDisplay.setAttribute('data-quantity', quantity);
  }

  /**
   * Get current quantity from DOM (single source of truth)
   */
  function getCurrentQuantity(cartItem) {
    // Cart page uses FSA hidden quantity input
    const cartInput = cartItem.querySelector('[data-fsa-hidden-quantity-cart]');
    if (!cartInput) {
      console.error('[Cart Yardage] Cart input not found');
      return 0;
    }
    const quantity = parseInt(cartInput.value, 10);
    console.log('[Cart Yardage] getCurrentQuantity returning:', quantity);
    return quantity || 0;
  }

  /**
   * Update cart quantity input to keep form submission in sync
   */
  function updateCartInput(cartItem, newQuantity) {
    // Update FSA hidden quantity input (this is what Shopify reads on checkout)
    const cartInput = cartItem.querySelector('[data-fsa-hidden-quantity-cart]');
    if (cartInput) {
      cartInput.value = newQuantity;
      console.log('[Cart Yardage] Updated cart input to:', newQuantity);
    }
  }

  /**
   * Show error message to user
   */
  function showError(cartItem, message) {
    // Remove any existing error messages
    const existingError = cartItem.querySelector('.cart-yardage-error');
    if (existingError) {
      existingError.remove();
    }

    // Create error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-yardage-error';
    errorDiv.style.cssText = 'color: #d9534f; font-size: 0.875rem; margin-top: 0.5rem;';
    errorDiv.textContent = message;
    errorDiv.setAttribute('role', 'alert');

    const selector = cartItem.querySelector('.cart-yardage-selector');
    if (selector && selector.parentNode) {
      selector.parentNode.insertBefore(errorDiv, selector.nextSibling);
    }

    // Auto-remove after 5 seconds
    setTimeout(() => errorDiv.remove(), 5000);
  }

  /**
   * Set button loading state
   */
  function setButtonsLoading(cartItem, isLoading) {
    const minusBtn = cartItem.querySelector('.cart-yardage-minus');
    const plusBtn = cartItem.querySelector('.cart-yardage-plus');

    if (minusBtn && plusBtn) {
      minusBtn.disabled = isLoading;
      plusBtn.disabled = isLoading;

      if (isLoading) {
        minusBtn.style.opacity = '0.5';
        plusBtn.style.opacity = '0.5';
      } else {
        minusBtn.style.opacity = '1';
        plusBtn.style.opacity = '1';
      }
    }
  }

  /**
   * Update cart quantity via Shopify AJAX API
   */
  function updateCartQuantity(cartItem, lineIndex, newQuantity, oldQuantity) {
    // DEBUG: Log what we're about to send
    console.log('[Cart Yardage] UPDATE REQUEST:', {
      lineIndex: lineIndex,
      newQuantity: newQuantity,
      oldQuantity: oldQuantity
    });

    // Prevent duplicate requests for the same line
    if (pendingRequests.has(lineIndex)) {
      console.log('[Cart Yardage] Request already pending for line', lineIndex);
      return;
    }

    // Disable buttons during request
    setButtonsLoading(cartItem, true);
    pendingRequests.set(lineIndex, true);

    // Calculate yardage for line item property
    const yards = newQuantity / CONFIG.yardsToUnitsMultiplier;
    const yardText = yards === 1 ? 'yard' : 'yards';
    const yardageProperty = `${yards} ${yardText}`;

    // Update both quantity AND properties in a single call
    const requestBody = {
      line: lineIndex,
      quantity: newQuantity,
      properties: {
        'Yardage': yardageProperty,
        '_is_fractional': 'true'
      }
    };
    console.log('[Cart Yardage] Sending to API:', JSON.stringify(requestBody));

    // Wrap fetch in retry logic
    retryWithBackoff(async () => {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        // Check for inventory error
        const errorText = await response.text();
        if (errorText.includes('inventory') || errorText.includes('available') || response.status === 422) {
          const error = new Error('Insufficient inventory available');
          error.isInventoryError = true;
          throw error;
        }
        throw new Error(`Cart update failed: ${response.status}`);
      }
      return response.json();
    })
    .then(cart => {
      console.log('[Cart Yardage] Cart updated successfully');
      console.log('[Cart Yardage] API RESPONSE:', {
        item_count: cart.item_count,
        items: cart.items.map(item => ({
          title: item.title,
          quantity: item.quantity,
          final_line_price: item.final_line_price,
          properties: item.properties
        }))
      });

      // Update cart input to match API success
      updateCartInput(cartItem, newQuantity);

      // Update cart totals without reloading
      updateCartTotals(cart, lineIndex);
    })
    .catch(error => {
      console.error('[Cart Yardage] Error updating cart:', error);

      // CRITICAL: Revert both display AND cart input to old quantity
      updateYardageDisplay(cartItem, oldQuantity);
      updateCartInput(cartItem, oldQuantity);

      // Show appropriate error message
      let errorMessage = 'Failed to update quantity. Please try again.';
      if (error.isInventoryError) {
        errorMessage = 'Not enough inventory available.';
        // Disable plus button when at max inventory
        const plusBtn = cartItem.querySelector('.cart-plus-btn');
        if (plusBtn) {
          plusBtn.disabled = true;
          plusBtn.style.opacity = '0.5';
          plusBtn.style.cursor = 'not-allowed';
        }
      }
      showError(cartItem, errorMessage);
    })
    .finally(() => {
      // Re-enable buttons
      setButtonsLoading(cartItem, false);
      pendingRequests.delete(lineIndex);
    });
  }

  /**
   * Update cart totals in the UI
   */
  function updateCartTotals(cart, lineIndex) {
    // Update the specific line item total (FIX: use correct index)
    const cartItems = document.querySelectorAll('.cart-item');
    const targetItem = cartItems[lineIndex - 1];

    if (targetItem && cart.items[lineIndex - 1]) {
      const cartItem = cart.items[lineIndex - 1];
      // Select the SPAN specifically (not the struck-out price div)
      const lineTotal = targetItem.querySelector('.cart-item__total > span.theme-money');

      if (lineTotal) {
        // Total column ALWAYS shows the total line price (quantity × price)
        // For 1.5 yards at $4.98/yard, this shows $7.47 total
        lineTotal.textContent = formatMoney(cartItem.final_line_price);
        console.log('[Cart Yardage] Updated line total to:', formatMoney(cartItem.final_line_price));
      } else {
        console.warn('[Cart Yardage] Could not find line total element for line', lineIndex);
      }
    }

    // Update cart subtotal
    const subtotal = document.querySelector('.subtotal .theme-money');
    if (subtotal) {
      subtotal.textContent = formatMoney(cart.total_price);
    }
  }

  /**
   * Format money - uses theme's currency format if available
   */
  function formatMoney(cents) {
    // Try to use theme's money formatting if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
    }

    // Fallback to simple USD format
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Handle +/- button clicks
   */
  function setupButtons(cartItem) {
    const minusBtn = cartItem.querySelector('.cart-yardage-minus');
    const plusBtn = cartItem.querySelector('.cart-yardage-plus');
    const yardageDisplay = cartItem.querySelector('.cart-yardage-display');
    const cartQuantityInput = cartItem.querySelector('[data-fsa-hidden-quantity-cart]');

    console.log('[Cart Yardage] Element check:', {
      minusBtn: !!minusBtn,
      plusBtn: !!plusBtn,
      yardageDisplay: !!yardageDisplay,
      cartQuantityInput: !!cartQuantityInput
    });

    if (!minusBtn || !plusBtn || !yardageDisplay || !cartQuantityInput) {
      console.log('[Cart Yardage] Missing required elements - skipping this item');
      return;
    }

    // Prevent duplicate initialization
    if (minusBtn.hasAttribute('data-yardage-initialized')) {
      console.log('[Cart Yardage] Already initialized, skipping');
      return;
    }
    minusBtn.setAttribute('data-yardage-initialized', 'true');
    plusBtn.setAttribute('data-yardage-initialized', 'true');

    // Get initial quantity from cart input (single source of truth)
    const initialQuantity = getCurrentQuantity(cartItem);
    console.log('[Cart Yardage] INIT - Cart quantity:', initialQuantity);

    // Update display to match actual cart quantity (fix any stale display)
    updateYardageDisplay(cartItem, initialQuantity);

    // Get line index from data attribute
    const lineIndex = parseInt(minusBtn.getAttribute('data-line'), 10);
    console.log('[Cart Yardage] INIT - Line index:', lineIndex);

    // Minus button
    minusBtn.addEventListener('click', (e) => {
      e.preventDefault();

      // Check if request already pending - if so, ignore click
      if (pendingRequests.has(lineIndex)) {
        console.log('[Cart Yardage] Click ignored - request already pending');
        return;
      }

      // Read current quantity from DOM (prevents race condition)
      const currentQuantity = getCurrentQuantity(cartItem);
      console.log('[Cart Yardage] MINUS CLICKED - Current:', currentQuantity);

      // Don't allow going below 0.5 yards (1 unit)
      const newQuantity = Math.max(1, currentQuantity - 1);

      if (newQuantity !== currentQuantity) {
        // Optimistically update display
        updateYardageDisplay(cartItem, newQuantity);

        // Update cart via AJAX
        updateCartQuantity(cartItem, lineIndex, newQuantity, currentQuantity);
      }
    });

    // Plus button
    plusBtn.addEventListener('click', (e) => {
      e.preventDefault();

      // Check if button is disabled (at max inventory)
      if (plusBtn.disabled) {
        console.log('[Cart Yardage] Plus button disabled - at max inventory');
        return;
      }

      // Check if request already pending - if so, ignore click
      if (pendingRequests.has(lineIndex)) {
        console.log('[Cart Yardage] Click ignored - request already pending');
        return;
      }

      // Read current quantity from DOM (prevents race condition)
      const currentQuantity = getCurrentQuantity(cartItem);
      console.log('[Cart Yardage] PLUS CLICKED - Current:', currentQuantity);

      const newQuantity = currentQuantity + 1;

      // Optimistically update display
      updateYardageDisplay(cartItem, newQuantity);

      // Update cart via AJAX
      updateCartQuantity(cartItem, lineIndex, newQuantity, currentQuantity);
    });

    // Listen to cart input changes (catches manual edits)
    cartQuantityInput.addEventListener('change', () => {
      const newQuantity = parseInt(cartQuantityInput.value) || 0;
      const oldQuantity = parseInt(yardageDisplay.getAttribute('data-quantity')) || 0;

      if (newQuantity !== oldQuantity && newQuantity > 0) {
        console.log('[Cart Yardage] Quantity changed externally:', oldQuantity, '→', newQuantity);

        // Update display
        updateYardageDisplay(cartItem, newQuantity);

        // Update cart with new Yardage property
        updateCartQuantity(cartItem, lineIndex, newQuantity, oldQuantity);
      }
    });

    console.log(`[Cart Yardage] Buttons set up for line ${lineIndex}, initial quantity: ${initialQuantity} units`);
  }

  /**
   * Check if an item is a fractional yardage product
   * Uses the _is_fractional property set at add-to-cart time
   */
  function isFractionalYardageProduct(item) {
    // Check for the _is_fractional property (most reliable method)
    if (item.properties && item.properties._is_fractional === "true") {
      return true;
    }

    // Fallback: check variant title for legacy items in cart
    const variantTitle = (item.variant_title || '').toLowerCase();
    const productTitle = (item.title || '').toLowerCase();

    const fractionalIndicators = [
      '1 yard',
      'half yard',
      '1/2 yard',
      '0.5 yard'
    ];

    return fractionalIndicators.some(indicator =>
      variantTitle.includes(indicator) || productTitle.includes(indicator)
    );
  }

  /**
   * Fix missing or incorrect Yardage properties on cart items
   */
  function fixMissingYardageProperties() {
    // Check if we just reloaded after fixing - prevent infinite loop
    const justFixed = sessionStorage.getItem('cart-yardage-just-fixed');
    if (justFixed) {
      console.log('[Cart Yardage] Already fixed properties on previous load, skipping check');
      sessionStorage.removeItem('cart-yardage-just-fixed');
      return;
    }

    // Quick DOM check - if no yardage items, skip API call entirely
    const hasYardageItems = document.querySelector('.cart-yardage-selector');
    if (!hasYardageItems) {
      console.log('[Cart Yardage] No yardage items in cart, skipping property check');
      return;
    }

    // Fetch current cart to check properties
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('[Cart Yardage] Checking for missing or incorrect Yardage properties...');

        const updatePromises = [];

        cart.items.forEach((item, index) => {
          const lineIndex = index + 1;
          const currentYardageProperty = item.properties && item.properties.Yardage;

          // Check if this is a fractional yardage product by variant title
          const isFractional = isFractionalYardageProduct(item);

          // Also check DOM for yardage selector (items already properly configured)
          const cartItems = document.querySelectorAll('.cart-item');
          const cartItem = cartItems[index];
          const hasYardageSelector = cartItem && cartItem.querySelector('.cart-yardage-selector');

          // Only process if this is a fractional yardage item
          if (hasYardageSelector || isFractional) {
            // Calculate what the yardage SHOULD be
            const correctYards = item.quantity / CONFIG.yardsToUnitsMultiplier;
            const yardText = correctYards === 1 ? 'yard' : 'yards';
            const correctYardageProperty = `${correctYards} ${yardText}`;

            // Check if property is missing or incorrect
            const isMissing = !currentYardageProperty;
            const isIncorrect = currentYardageProperty && currentYardageProperty !== correctYardageProperty;

            if (isMissing) {
              console.log(`[Cart Yardage] Item "${item.title}" (${item.variant_title}) missing Yardage property - fixing...`);
            } else if (isIncorrect) {
              console.log(`[Cart Yardage] Item "${item.title}" (${item.variant_title}) has incorrect Yardage: "${currentYardageProperty}" should be "${correctYardageProperty}" - fixing...`);
            }

            if (isMissing || isIncorrect) {
              // Build properties object - always include Yardage and _is_fractional
              const updatedProperties = {
                'Yardage': correctYardageProperty,
                '_is_fractional': 'true'
              };

              // Preserve any other existing properties
              if (item.properties) {
                for (const [key, value] of Object.entries(item.properties)) {
                  if (key !== 'Yardage' && key !== '_is_fractional' && !key.startsWith('_')) {
                    updatedProperties[key] = value;
                  }
                }
              }

              // Create promise for this update
              const updatePromise = fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  line: lineIndex,
                  quantity: item.quantity,
                  properties: updatedProperties
                })
              })
              .then(response => response.json())
              .then(() => {
                console.log(`[Cart Yardage] ${isMissing ? 'Added' : 'Corrected'} Yardage property: ${correctYardageProperty}`);
              })
              .catch(error => {
                console.error('[Cart Yardage] Failed to update Yardage property:', error);
                throw error; // Re-throw to handle in Promise.all
              });

              updatePromises.push(updatePromise);
            }
          }
        });

        // Wait for ALL updates to complete, then reload
        if (updatePromises.length > 0) {
          Promise.all(updatePromises)
            .then(() => {
              console.log(`[Cart Yardage] All ${updatePromises.length} property updates complete, reloading page...`);
              // Set flag to prevent infinite loop
              sessionStorage.setItem('cart-yardage-just-fixed', 'true');
              window.location.reload();
            })
            .catch(error => {
              console.error('[Cart Yardage] Some updates failed, not reloading to prevent issues:', error);
            });
        } else {
          console.log('[Cart Yardage] All properties correct, no updates needed');
        }
      })
      .catch(error => {
        console.error('[Cart Yardage] Failed to fetch cart:', error);
      });
  }

  /**
   * Initialize yardage updater
   */
  function initYardageUpdater() {
    const cartItems = document.querySelectorAll('.cart-item');

    console.log(`[Cart Yardage] Found ${cartItems.length} cart items`);

    // Fix any missing Yardage properties first
    fixMissingYardageProperties();

    cartItems.forEach(cartItem => {
      // Only set up if yardage selector exists and not already initialized
      if (cartItem.querySelector('.cart-yardage-selector')) {
        setupButtons(cartItem);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initYardageUpdater);
  } else {
    initYardageUpdater();
  }

  // Re-initialize for Shopify section updates (theme editor)
  // setupButtons() now has duplicate protection via data-yardage-initialized attribute
  document.addEventListener('shopify:section:load', initYardageUpdater);

})();
